import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../services/api.service';
import logger from '../utils/logger';

const NotificationsDropdown = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen]);

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await notificationService.getNotifications();
            setNotifications(response.notifications || []);
        } catch (error) {
            logger.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const markAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();
            setNotifications(notifications.map(n => ({ ...n, read: true })));
            onClose(); // سيقوم بتحديث العداد في الصفحة الرئيسية
        } catch (error) {
            logger.error('Error marking all notifications as read:', error);
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            await notificationService.markAsRead(notificationId);
            setNotifications(notifications.map(n => 
                n.id === notificationId ? { ...n, read: true } : n
            ));
        } catch (error) {
            logger.error('Error marking notification as read:', error);
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'CASE_UPDATE': return '⚖️';
            case 'SESSION': return '📅';
            case 'PAYMENT': return '💰';
            case 'DOCUMENT': return '📄';
            case 'warning': return '⚠️';
            case 'info': return 'ℹ️';
            default: return '🔔';
        }
    };

    const getTimeAgo = (date) => {
        const now = new Date();
        const diff = now - new Date(date);
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `منذ ${days} يوم${days > 1 ? 'اً' : ''}`;
        if (hours > 0) return `منذ ${hours} ساعة`;
        if (minutes > 0) return `منذ ${minutes} دقيقة`;
        return 'الآن';
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute left-0 mt-2 w-96 z-50"
                >
                    <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="font-semibold text-gray-800">الإشعارات</h3>
                            <button 
                                onClick={markAllAsRead}
                                className="text-sm text-blue-600 hover:text-blue-800"
                            >
                                تحديد الكل كمقروء
                            </button>
                        </div>

                        <div className="max-h-[70vh] overflow-y-auto">
                            {loading ? (
                                <div className="p-4 text-center text-gray-500">جاري التحميل...</div>
                            ) : notifications.length === 0 ? (
                                <div className="p-4 text-center text-gray-500">لا توجد إشعارات جديدة</div>
                            ) : (
                                notifications.map(notification => (
                                    <motion.div
                                        key={notification.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        onClick={() => {
                                            markAsRead(notification.id);
                                            if (notification.link) navigate(notification.link);
                                        }}
                                        className={`
                                            p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer
                                            ${!notification.is_read ? 'bg-blue-50' : ''}
                                        `}
                                    >
                                        <div className="flex gap-3">
                                            <span className="text-2xl">
                                                {getNotificationIcon(notification.type)}
                                            </span>
                                            <div className="flex-1">
                                                <h4 className="font-medium text-gray-800 mb-1">
                                                    {notification.title}
                                                </h4>
                                                <p className="text-sm text-gray-600 mb-2">
                                                    {notification.message || notification.event_title || notification.title}
                                                </p>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-xs text-gray-500">
                                                        {getTimeAgo(notification.created_at)}
                                                    </span>
                                                    {!notification.is_read && (
                                                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                            جديد
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </div>

                        <div className="p-4 border-t border-gray-100 bg-gray-50">
                            <button 
                                onClick={() => {
                                    navigate('/notifications');
                                    onClose();
                                }}
                                className="w-full text-center text-sm text-blue-600 hover:text-blue-800"
                            >
                                عرض كل الإشعارات
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default NotificationsDropdown;
